import React from 'react';
import { InputProps } from '@material-ui/core';

import { useIntl } from 'ha/i18n';
import Locations, { MIN_SEARCH_INPUT_LENGTH } from 'ha/modules/Locations';
import { SimpleItem } from 'ha/components/next/LocationAutocompleteInput';

import CityTextField from './CityTextField';

export interface Props {
  value: string;
  onChange: (value: string) => void;
  initialItems: SimpleItem[];
  changeOnlyOnSelect?: boolean;
}

const PredictionTypes: string[] = ['(cities)'];

const PlaceFilter: React.FC<Props> = ({
  value,
  onChange,
  initialItems,
  changeOnlyOnSelect,
}) => {
  const { T } = useIntl();

  const [inputValue, setInputValue] = React.useState(value);

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  const [renderInitialItems, setRenderInitialItems] = React.useState(false);

  React.useEffect(() => {
    const length = value?.length ?? 0;

    // User erased all symbols, lets show initial suggestions
    if (length === 0) {
      setRenderInitialItems(true);
    }

    // User typed MIN_SEARCH_INPUT_LENGTH letters
    // lets hide initial suggestions and show ones from google
    if (length >= MIN_SEARCH_INPUT_LENGTH && renderInitialItems) {
      setRenderInitialItems(false);
    }
  }, [renderInitialItems, value]);

  const handleSelect = React.useCallback(
    (newValue: string) => {
      onChange(newValue);
    },
    [onChange],
  );

  const handleChange = React.useCallback(
    (newValue: string) => {
      if (changeOnlyOnSelect) {
        setInputValue(newValue);
      } else {
        onChange(newValue);
      }
    },
    [changeOnlyOnSelect, onChange],
  );

  return (
    <Locations
      renderInput={inputProps => {
        const {
          ref,
          onChange: onChangeInput,
          onFocus,
          onBlur,
          ...other
        } = inputProps;

        return (
          <CityTextField
            margin="dense"
            fullWidth
            variant="outlined"
            value={inputValue}
            InputProps={{
              inputRef: ref as React.RefObject<HTMLInputElement>,
              ...(other as InputProps),
            }}
            onChange={onChangeInput}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={T('Where will you go?')}
          />
        );
      }}
      value={inputValue}
      predictionTypes={PredictionTypes}
      onSelect={handleSelect}
      onChange={handleChange}
      renderInitialItems={renderInitialItems}
      initialItems={initialItems}
    />
  );
};

export default PlaceFilter;
